import type { AppError } from '@core/api/normalizeError'
import type {
  BuyQuoteDto,
  EnvelopeDto,
  FeeLineDto,
  MarketplaceListingDetailDto,
  MarketplaceListingDto,
  MarketplacePropertyCardDto,
  MarketplacePropertyDetailDto,
  MyListingDetailDto,
  MyListingDto,
  PaginatedEnvelopeDto,
  PaginationDto,
  SecondaryOrderDetailDto,
  SecondaryOrderSummaryDto,
  SellOverviewDto,
  SellQuoteDto,
} from './tradingContracts'
import {
  MY_LISTING_STATUS,
  SECONDARY_ORDER_STATUS,
  SETTLEMENT_STEPS,
  type BuyQuote,
  type ListingFill,
  type MarketProperty,
  type MarketPropertyCard,
  type MyListing,
  type MyListingDetail,
  type MyListingStatus,
  type SecondaryOrder,
  type SecondaryOrderDetail,
  type SecondaryOrderStatus,
  type SellOverview,
  type SellQuote,
  type SellerListing,
  type SellerListingDetail,
  type SettlementStep,
  type TradeFeeLine,
  type TradingPage,
} from '../types/tradingTypes'

// Wire DTO → view model for the secondary market. Decimal strings become
// numbers, nullable server fields stay null (never invented), unknown enum
// values fall back to a safe member so a new server status can't crash a
// status table lookup.

// ── Primitives ────────────────────────────────────────────────────────────

const toNumber = (raw: string | number | null | undefined): number => {
  const value = Number(raw ?? 0)
  return Number.isFinite(value) ? value : 0
}

const toNullableNumber = (raw: string | number | null | undefined): number | null => {
  if (raw === null || raw === undefined || raw === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

const toOptionalCount = (raw: number | null | undefined): number | undefined =>
  raw != null && Number.isFinite(Number(raw)) ? Number(raw) : undefined

const toOptionalDate = (raw: string | null | undefined): string | undefined => raw || undefined

const EMPTY_RESPONSE_MESSAGE = 'We received an empty response. Please try again.'

/** Unwraps the `{ success, message, data }` envelope; an empty one is an error. */
export const requireData = <T>(response: EnvelopeDto<T> | undefined): T => {
  const data = response?.data
  if (data === undefined || data === null) {
    const failure: AppError = { message: EMPTY_RESPONSE_MESSAGE }
    throw failure
  }
  return data
}

const EMPTY_PAGINATION: PaginationDto = { page: 1, limit: 0, totalItems: 0, totalPages: 1 }

const parsePage = <D, V>(
  response: PaginatedEnvelopeDto<D> | undefined,
  parseRow: (row: D) => V,
): TradingPage<V> => ({
  rows: (response?.data ?? []).map(parseRow),
  pagination: response?.pagination ?? EMPTY_PAGINATION,
})

const areaFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

/** "1250.00" → "1,250 sqm"; a non-numeric label passes through. */
const formatArea = (raw: string | null): string | undefined => {
  if (!raw?.trim()) return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? `${areaFormatter.format(value)} sqm` : raw.trim()
}

/** "apartment" / "store_unit" → "Apartment" / "Store Unit". */
const formatPropertyType = (raw: string | null): string | undefined =>
  raw
    ?.split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ') || undefined

const ORDER_STATUSES = new Set<string>(Object.values(SECONDARY_ORDER_STATUS))
const LISTING_STATUSES = new Set<string>(Object.values(MY_LISTING_STATUS))

const isOrderStatus = (raw: string): raw is SecondaryOrderStatus => ORDER_STATUSES.has(raw)
const isListingStatus = (raw: string): raw is MyListingStatus => LISTING_STATUSES.has(raw)
const isSettlementStep = (raw: string): raw is SettlementStep =>
  (SETTLEMENT_STEPS as readonly string[]).includes(raw)

// Unknown values read as the earliest (most conservative) state.
const toOrderStatus = (raw: string): SecondaryOrderStatus =>
  isOrderStatus(raw) ? raw : SECONDARY_ORDER_STATUS.PENDING
const toListingStatus = (raw: string): MyListingStatus =>
  isListingStatus(raw) ? raw : MY_LISTING_STATUS.UNDER_REVIEW

export const parseFeeLine = (line: FeeLineDto): TradeFeeLine => ({
  code: line.code,
  label: line.label,
  rate: line.rate?.trim() || null,
  amount: toNumber(line.amount),
  vatAmount: toNumber(line.vatAmount),
})

const parseFeeLines = (lines: FeeLineDto[] | null | undefined): TradeFeeLine[] =>
  (lines ?? []).map(parseFeeLine)

// ── Properties ────────────────────────────────────────────────────────────

export const parseMarketPropertyCard = (dto: MarketplacePropertyCardDto): MarketPropertyCard => ({
  id: dto.propertyId,
  offeringId: dto.offeringId,
  title: dto.title,
  location: dto.location ?? '',
  image: dto.coverImageUrl ?? '',
  propertyType: formatPropertyType(dto.propertyType),
  bedrooms: toOptionalCount(dto.bedrooms),
  bathrooms: toOptionalCount(dto.bathrooms),
  area: formatArea(dto.area),
  totalTokens: toNumber(dto.totalTokens),
  valuation: toNullableNumber(dto.valuationPerToken),
  valuationDate: dto.valuationDate,
  lowestPrice: toNumber(dto.lowestPricePerToken),
  averagePrice: toNumber(dto.averagePricePerToken),
  tokensAvailable: toNumber(dto.tokensAvailable),
  tokensListed: toNumber(dto.tokensListed),
  tokensSold: toNumber(dto.tokensSold),
  sellerCount: toNumber(dto.sellerCount),
  listingCount: toNumber(dto.listingCount),
  bestDiscountPct: toNullableNumber(dto.bestDiscountPct),
  isOwned: Boolean(dto.isOwned),
  tradingPaused: Boolean(dto.tradingPaused),
  tradingPausedReason: dto.tradingPausedReason?.trim() || null,
  latestListedAt: dto.latestListedAt,
})

export const parseMarketPropertyPage = (
  response: PaginatedEnvelopeDto<MarketplacePropertyCardDto> | undefined,
): TradingPage<MarketPropertyCard> => parsePage(response, parseMarketPropertyCard)

export const parseMarketProperty = (dto: MarketplacePropertyDetailDto): MarketProperty => ({
  ...parseMarketPropertyCard(dto),
  isTradable: Boolean(dto.isTradable),
  bandPct: toNumber(dto.bandPct),
  bandMin: toNullableNumber(dto.bandFloor),
  bandMax: toNullableNumber(dto.bandCeiling),
  minOrderAmount: toNumber(dto.minOrderAmount),
  ownershipCapPct: toNumber(dto.ownershipCapPct),
  ownershipCapTokens: toNumber(dto.ownershipCapTokens),
  tokensHeld: toNumber(dto.tokensHeld),
})

// ── Listings + buy quote ──────────────────────────────────────────────────

export const parseSellerListing = (dto: MarketplaceListingDto): SellerListing => ({
  id: dto.listingId,
  reference: dto.listingReference || dto.listingId,
  propertyId: dto.propertyId,
  pricePerToken: toNumber(dto.pricePerToken),
  tokensAvailable: toNumber(dto.tokensAvailable),
  variancePct: toNullableNumber(dto.variancePct),
  minPurchaseTokens: toNumber(dto.minPurchaseTokens),
  minOrderAmount: toNumber(dto.minOrderAmount),
  listedAt: dto.publishedAt,
  isOwnListing: Boolean(dto.isOwnListing),
})

export const parseSellerListings = (dtos: MarketplaceListingDto[] | undefined): SellerListing[] =>
  (dtos ?? []).map(parseSellerListing)

export const parseSellerListingDetail = (
  dto: MarketplaceListingDetailDto,
): SellerListingDetail => ({
  ...parseSellerListing(dto),
  propertyTitle: dto.propertyTitle,
  offeringId: dto.offeringId,
})

export const parseBuyQuote = (dto: BuyQuoteDto): BuyQuote => ({
  listingId: dto.listingId,
  tokens: toNumber(dto.tokenQuantity),
  pricePerToken: toNumber(dto.pricePerToken),
  investmentValue: toNumber(dto.investmentValue),
  platformFee: toNumber(dto.platformFee),
  dldFee: toNumber(dto.dldFee),
  vat: toNumber(dto.vat),
  totalPayable: toNumber(dto.totalPayable),
  fees: parseFeeLines(dto.fees),
  minTokens: toNumber(dto.minTokens),
  maxTokens: toNumber(dto.maxTokens),
  tokensAvailable: toNumber(dto.tokensAvailable),
  tokensHeld: toNumber(dto.tokensHeld),
  ownershipCapTokens: toNumber(dto.ownershipCapTokens),
  walletAvailableBalance: toNumber(dto.walletAvailableBalance),
  isSufficientBalance: Boolean(dto.isSufficientBalance),
  errors: dto.errors ?? [],
})

// ── Orders ────────────────────────────────────────────────────────────────

export const parseSecondaryOrder = (dto: SecondaryOrderSummaryDto): SecondaryOrder => ({
  id: dto.orderId,
  reference: dto.tradeReference || dto.orderId,
  side: dto.side === 'sell' ? 'sell' : 'buy',
  status: toOrderStatus(dto.status),
  statusLabel: dto.statusLabel,
  propertyId: dto.propertyId,
  propertyTitle: dto.propertyTitle,
  image: dto.coverImageUrl ?? '',
  listingId: dto.listingId,
  listingReference: dto.listingReference || dto.listingId,
  tokens: toNumber(dto.tokenQuantity),
  pricePerToken: toNumber(dto.pricePerToken),
  investmentValue: toNumber(dto.investmentValue),
  totalPayable: toNullableNumber(dto.totalPayable),
  netProceeds: toNullableNumber(dto.netProceeds),
  isCancellable: Boolean(dto.isCancellable),
  placedAt: dto.placedAt,
  updatedAt: dto.updatedAt,
})

export const parseSecondaryOrderPage = (
  response: PaginatedEnvelopeDto<SecondaryOrderSummaryDto> | undefined,
): TradingPage<SecondaryOrder> => parsePage(response, parseSecondaryOrder)

/** Reached-step timestamps + the step a rejected trade failed at. */
const parseSteps = (
  steps: SecondaryOrderDetailDto['steps'] | null | undefined,
): Pick<SecondaryOrderDetail, 'stepDates' | 'failedStep'> => {
  const stepDates: Partial<Record<SettlementStep, string>> = {}
  let failedStep: SettlementStep | undefined
  for (const step of steps ?? []) {
    if (!isSettlementStep(step.status)) continue
    if (step.at) stepDates[step.status] = step.at
    if (step.isFailed) failedStep = step.status
  }
  return { stepDates, failedStep }
}

export const parseSecondaryOrderDetail = (dto: SecondaryOrderDetailDto): SecondaryOrderDetail => ({
  ...parseSecondaryOrder(dto),
  variancePct: toNullableNumber(dto.variancePct),
  ownershipPct: toNullableNumber(dto.ownershipPctAfter),
  platformFee: toNumber(dto.platformFee),
  dldFee: toNullableNumber(dto.dldFee),
  performanceFee: toNullableNumber(dto.performanceFee),
  vat: toNumber(dto.vat),
  fees: parseFeeLines(dto.fees),
  statusReason: dto.statusReason?.trim() || null,
  ...parseSteps(dto.steps),
  completedAt: dto.completedAt,
  cancelledAt: dto.cancelledAt,
  rejectedAt: dto.rejectedAt,
})

// ── My listings ───────────────────────────────────────────────────────────

export const parseMyListing = (dto: MyListingDto): MyListing => ({
  id: dto.listingId,
  reference: dto.listingReference || dto.listingId,
  propertyId: dto.propertyId,
  propertyTitle: dto.propertyTitle,
  image: dto.coverImageUrl ?? '',
  status: toListingStatus(dto.status),
  statusReason: dto.statusReason?.trim() || null,
  rejectionReason: dto.rejectionReason?.trim() || null,
  tokensListed: toNumber(dto.tokensListed),
  tokensSold: toNumber(dto.tokensSold),
  tokensInActiveOrders: toNumber(dto.tokensReserved),
  tokensRemaining: toNumber(dto.tokensRemaining),
  pricePerToken: toNumber(dto.pricePerToken),
  variancePct: toNullableNumber(dto.variancePct),
  bandMin: toNumber(dto.bandFloor),
  bandMax: toNumber(dto.bandCeiling),
  acquisitionBasis: toNumber(dto.acquisitionBasis),
  proceedsToDate: toNumber(dto.proceedsToDate),
  createdAt: dto.createdAt,
  publishedAt: toOptionalDate(dto.publishedAt),
  expiresAt: toOptionalDate(dto.expiresAt),
  closedAt: toOptionalDate(dto.closedAt),
  canCancel: Boolean(dto.canCancel),
  canRenew: Boolean(dto.canRenew),
  renewalWindowOpensAt: dto.renewalWindowOpensAt,
})

export const parseMyListingPage = (
  response: PaginatedEnvelopeDto<MyListingDto> | undefined,
): TradingPage<MyListing> => parsePage(response, parseMyListing)

const parseFill = (fill: MyListingDetailDto['fills'][number]): ListingFill => ({
  id: fill.tradeReference,
  buyer: fill.buyerLabel,
  tokens: toNumber(fill.tokens),
  status: toOrderStatus(fill.status),
  statusLabel: fill.statusLabel,
  date: fill.placedAt,
  completedAt: fill.completedAt,
})

export const parseMyListingDetail = (dto: MyListingDetailDto): MyListingDetail => ({
  ...parseMyListing(dto),
  fills: (dto.fills ?? []).map(parseFill),
  history: (dto.history ?? []).map((entry) => ({
    status: toListingStatus(entry.status),
    at: entry.at,
    actor: entry.actor,
    reason: entry.reason?.trim() || null,
  })),
})

// ── Sell ──────────────────────────────────────────────────────────────────

export const parseSellOverview = (dto: SellOverviewDto): SellOverview => ({
  propertyId: dto.propertyId,
  offeringId: dto.offeringId,
  propertyTitle: dto.propertyTitle,
  image: dto.coverImageUrl ?? '',
  isTradable: Boolean(dto.isTradable),
  canSell: Boolean(dto.canSell),
  ineligibleReasons: dto.ineligibleReasons ?? [],
  tokensHeld: toNumber(dto.tokensHeld),
  tokensLocked: toNumber(dto.tokensLocked),
  tokensSellable: toNumber(dto.tokensSellable),
  tokensInLockIn: toNumber(dto.tokensInLockIn),
  lockInEndsAt: dto.lockInEndsAt,
  averageCost: toNumber(dto.averageCostPerToken),
  valuation: toNullableNumber(dto.valuationPerToken),
  valuationDate: dto.valuationDate,
  appreciationPerToken: toNullableNumber(dto.appreciationPerToken),
  appreciationPct: toNullableNumber(dto.appreciationPct),
  bandPct: toNumber(dto.bandPct),
  bandMin: toNullableNumber(dto.bandFloor),
  bandMax: toNullableNumber(dto.bandCeiling),
  minListingAmount: toNumber(dto.minListingAmount),
  isSellAllRemainingEligible: Boolean(dto.isSellAllRemainingEligible),
})

export const parseSellQuote = (dto: SellQuoteDto): SellQuote => ({
  tokens: toNumber(dto.tokenQuantity),
  pricePerToken: toNumber(dto.pricePerToken),
  grossAmount: toNumber(dto.grossAmount),
  platformFee: toNumber(dto.platformFee),
  performanceFee: toNumber(dto.performanceFee),
  vat: toNumber(dto.vat),
  netProceeds: toNumber(dto.netProceeds),
  fees: parseFeeLines(dto.fees),
  variancePct: toNullableNumber(dto.variancePct),
  isWithinBand: Boolean(dto.isWithinBand),
  isSellAllRemaining: Boolean(dto.isSellAllRemaining),
  errors: dto.errors ?? [],
})
